import React, { ChangeEvent, useEffect, useState } from 'react'
import AdSlot from '../components/AdSlot'
import EzoicAdBatch from '../components/EzoicAdBatch'
import NewsItem from '../components/NewsItem'
import SEO from '../components/SEO'
import { fetchNews } from '../services/api'
import '../styles/Home.scss'

interface News {
  id: string
  title: string
  summary: string
  image_url: string
  source_url: string
  published_at: string
  slug: string
}

const HOME_PLACEMENT_IDS = [111, 112, 113, 114, 115] as const

const Home: React.FC = () => {
  const [news, setNews] = useState<News[]>([])
  const [loading, setLoading] = useState<boolean>(true)
  const [currentPage, setCurrentPage] = useState<number>(1)
  const [searchQuery, setSearchQuery] = useState<string>('') // LOCAL

  const itemsPerPage = 10

  useEffect(() => {
    const loadNews = async () => {
      try {
        const fetchedNews: News[] = await fetchNews()
        setNews(fetchedNews)
      } catch (error) {
        console.error('Erro ao carregar notícias:', error)
      } finally {
        setLoading(false)
      }
    }

    loadNews()
  }, [])

  const handleSearchChange = (e: ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value)
  }

  // Filtra as notícias com base na busca
  const filteredNews = news.filter(
    (item) =>
      item.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      item.summary.toLowerCase().includes(searchQuery.toLowerCase())
  )

  const totalPages = Math.ceil(filteredNews.length / itemsPerPage)
  const indexOfLastItem = currentPage * itemsPerPage
  const indexOfFirstItem = indexOfLastItem - itemsPerPage
  const currentNews = filteredNews.slice(indexOfFirstItem, indexOfLastItem)

  // Lógica para exibir grupos de 5 páginas
  const maxPagesToShow = 5
  const startPage =
    Math.floor((currentPage - 1) / maxPagesToShow) * maxPagesToShow + 1
  const endPage = Math.min(startPage + maxPagesToShow - 1, totalPages)

  const pagesToShow: number[] = []
  for (let page = startPage; page <= endPage; page++) {
    pagesToShow.push(page)
  }

  const handlePageChange = (page: number) => {
    setCurrentPage(page)
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  const shouldRenderAds = !loading && filteredNews.length > 0

  return (
    <>
      <SEO
        title="Resumi Pra Ti - Notícias atualizadas e resumidas"
        description="Fique atualizado com os destaques do dia! Resumos precisos das notícias mais recentes, para você acessar as informações essenciais rapidamente."
        url="https://resumiprati.com.br/"
        image="https://resumiprati.com.br/assets/logo.webp"
      />

      <div className="home">
        <div className="home__search">
          <div className="home__search__wrapper">
            <img src="/assets/header-lupa.webp" alt="Buscar notícias" />
            <input
              type="text"
              placeholder="Buscar notícias..."
              aria-label="Buscar notícias"
              value={searchQuery}
              onChange={handleSearchChange}
            />
          </div>
        </div>

        {/* Ezoic: uma chamada por página para todos os slots existentes na Home */}
        {shouldRenderAds && <EzoicAdBatch placementIds={[...HOME_PLACEMENT_IDS]} />}

        {/* Ezoic: slot logo abaixo da busca */}
        {shouldRenderAds && (
          <div>
            <AdSlot placementId={111} />
          </div>
        )}

        {loading ? (
          <div>Carregando resumo de notícias...</div>
        ) : filteredNews.length === 0 ? (
          <div>OOOPS... SITE EM MANUTENÇÃO.</div>
        ) : (
          <>
            {currentNews.map((item, index) => (
              <React.Fragment key={item.id}>
                <NewsItem
                  id={item.id}
                  title={item.title}
                  date={item.published_at}
                  summary={item.summary}
                  image={item.image_url}
                  sourceUrl={item.source_url}
                  slug={item.slug}
                />

                {/* Ezoic: slots em posições fixas na lista, sempre no fluxo */}
                {index === 2 && (
                  <div>
                    <AdSlot placementId={112} />
                  </div>
                )}

                {index === 5 && (
                  <div>
                    <AdSlot placementId={113} />
                  </div>
                )}

                {index === 8 && (
                  <div>
                    <AdSlot placementId={114} />
                  </div>
                )}
              </React.Fragment>
            ))}

            {/* Ezoic: slot antes da paginação (se houver) */}
            {totalPages > 1 && (
              <div>
                <AdSlot placementId={115} />
              </div>
            )}

            {totalPages > 1 && (
              <div className="pagination">
                {startPage > 1 && (
                  <button onClick={() => handlePageChange(startPage - 1)}>
                    {'<'}
                  </button>
                )}

                {pagesToShow.map((page) => (
                  <button
                    key={page}
                    onClick={() => handlePageChange(page)}
                    className={page === currentPage ? 'active' : ''}
                  >
                    {page}
                  </button>
                ))}

                {endPage < totalPages && (
                  <button onClick={() => handlePageChange(endPage + 1)}>
                    {'>'}
                  </button>
                )}
              </div>
            )}
          </>
        )}
      </div>
    </>
  )
}

export default Home
